import { useMutation, useQuery } from "@tanstack/react-query";
import { getDepartments, getExistingRegistration, getSponsorshipTypes, submitRegistration } from "./resident.api";
import { Button } from "@/components/ui/button";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { registrationSchema } from "./registration.schema";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { SelectField } from "@/components/ui/select";
import { DashboardLayout } from "@/app/layout/DashboardLayout";
import { useEffect, useState } from "react";
import useGcpsUIController from "@/hooks/useGlobalContext";
import TextArea from "@/components/ui/textarea";
import { toaster } from "@/components/Toaster";
import { useNavigate } from "react-router-dom";
import { routes } from "@/app/routes/paths";
import { RegistrationSuccessPage } from "./RegistrationSuccessPage";
import { RegistrationClosedPage } from "./RegistrationClosedPage";
import { handleMutationError } from "@/lib/utils";

export const RegistrationPage = () => {
	const { setPageTitle, setPageLoading, controller } = useGcpsUIController();
	const { config, resident } = controller;
	const [departments, setDepartments] = useState<{ id: string; name: string }[]>([]);
	const [sponsorshipTypes, setSponsorshipTypes] = useState<{ id: string; name: string }[]>([]);
	const [extensionStartDate, setExtensionStartDate] = useState("");
	const [extensionEndDate, setExtensionEndDate] = useState("");
	const [defermentStartDate, setDefermentStartDate] = useState("");
	const [defermentEndDate, setDefermentEndDate] = useState("");
	const [rotationStartDate, setRotationStartDate] = useState("");
	const [rotationEndDate, setRotationEndDate] = useState("");
	const [existingRegistration, setExistingRegistration] = useState<{
		[x: string]: {
			[x: string]: {
				[x: string]: string
			}
		}
	} | null>();

	const navigate = useNavigate();

	const mutation = useMutation({
		mutationFn: submitRegistration,
		onSuccess: () => {
			toaster('success', 'Annual registration submitted successfully');
			navigate(routes.BASE_APP_URL)
		},
		onError: handleMutationError
	});

	const form = useForm({
		resolver: zodResolver(registrationSchema),
		shouldUnregister: false,
		defaultValues: {
			resident_number: resident?.resident_number || "",
			first_name: resident?.first_name || "",
			middle_name: resident?.other_name || "",
			surname: resident?.last_name || "",
			email: resident?.email || "",
			college_email: resident?.college_email || "",
			resident_status: resident?.resident_status || "",
			faculty: resident?.faculty || "",
			subspecialty: resident?.subspecialty || "",
			training_center: resident?.training_center || "",
			department: resident?.department || "",
			department_other: undefined,
			academic_year: config?.global.academic_period || "",
			current_year: undefined,
			extension_requested: undefined,
			extension_start_date: "",
			extension_end_date: "",
			deferment_requested: undefined,
			deferment_start_date: "",
			deferment_end_date: "",
			external_rotation: undefined,
			external_start_date: "",
			external_end_date: "",
			exam_resit: undefined,
			sponsoring_facility: undefined,
			tuition_receipt: null,
			training_challenges: "",
		},
	});

	useEffect(() => {
		setPageTitle('Annual Registration')
	}, [])

	const departmentValue = form.watch("department");
	const sponsoringValue = form.watch("sponsoring_facility");
	const extensionValue = form.watch("extension_requested");
	const deferrmentValue = form.watch("deferment_requested");
	const rotationValue = form.watch("external_rotation");

	const { data: departmentsData, isLoading: isDepartmentLoading } = useQuery({
		queryKey: ["departments"],
		queryFn: getDepartments,
	});

	useEffect(() => {
		if (departmentsData) {
			setDepartments(departmentsData.data.map((item: { id: string; name: string }) => ({ id: item.id.toString(), name: item.name })));
		}

	}, [departmentsData]);

	const { data: sponsorshipTypesData, isLoading: isSponsorshipTypesLoading } = useQuery({
		queryKey: ["sponsorship-types"],
		queryFn: getSponsorshipTypes,
	});

	useEffect(() => {
		if (sponsorshipTypesData) {
			setSponsorshipTypes(sponsorshipTypesData.data.map((item: { id: string; name: string }) => ({ id: item.id.toString(), name: item.name })));
		}

	}, [sponsorshipTypesData]);

	useEffect(() => {
		if (extensionStartDate) {
			form.setValue('extension_start_date', extensionStartDate);
		}
		if (extensionEndDate) {
			form.setValue('extension_end_date', extensionEndDate);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [extensionStartDate, extensionEndDate]);

	useEffect(() => {
		if (defermentStartDate) {
			form.setValue('deferment_start_date', defermentStartDate);
		}

		if (defermentEndDate) {
			form.setValue('deferment_end_date', defermentEndDate);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [defermentStartDate, defermentEndDate]);

	useEffect(() => {
		if (rotationStartDate) {
			form.setValue('external_start_date', rotationStartDate);
		}

		if (rotationEndDate) {
			form.setValue('external_end_date', rotationEndDate);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rotationStartDate, rotationEndDate]);

	const { data: regExist, isLoading: isRegExistLoading } = useQuery({
		queryKey: ["registered"],
		queryFn: getExistingRegistration,
	});

	useEffect(() => {
		setExistingRegistration(regExist);

	}, [regExist]);

	useEffect(() => {
		if (isRegExistLoading) {
			setPageLoading(true);
		} else {
			setPageLoading(false)
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isRegExistLoading]);

	return (
		<DashboardLayout>
			<div className="space-y-6">
				{
					existingRegistration !== null && existingRegistration?.data?.has_existing_registration ? (
						<RegistrationSuccessPage period={config?.global.academic_period || ""} />
					) : existingRegistration !== null && !existingRegistration?.data ? (
						<RegistrationClosedPage period={config?.global.academic_period || ""} />
					) : (
						<>
							<h1 className="text-2xl font-bold">{existingRegistration?.data?.registration_period?.name}</h1>
							<Form {...form}>
								<form onSubmit={form.handleSubmit((values) => {
									const formData = new FormData();

									Object.entries(values).forEach(([key, value]) => {
										if (value === undefined || value === null) return;

										if (key === "tuition_receipt" && value instanceof File) {
											formData.append("tuition_receipt", value);
										} else {
											formData.append(key, value as string);
										}
									});

									mutation.mutate(formData);
								}, console.log)} className="space-y-8" encType="multipart">
									<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
										<FormField
											control={form.control}
											name="resident_number"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<Input placeholder="01XXXX" {...field} label="Resident Number" disabled />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name="first_name"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<Input {...field} label="First Name" placeholder="John" className="uppercase" disabled />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name="middle_name"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<Input {...field} label="Middle Name" placeholder="Optional" className="uppercase" disabled />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name="surname"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<Input {...field} label="Surname" placeholder="Doe" className="uppercase" disabled />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name="gender"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<SelectField onValueChange={field.onChange} defaultValue={field.value}
															className="uppercase"
															label="Gender"
															placeholder="Select gender"
															options={[
																{ id: "Male", name: "Male" },
																{ id: "Female", name: "Female" },
															]}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name="email"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<Input type="email" {...field} label="Email" placeholder="john.doe@email.com" className="uppercase" disabled />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name="college_email"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<Input type="email" {...field} label="College Email" placeholder="john.doe@gcps.edu.gh" className="uppercase" />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name="resident_status"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<SelectField onValueChange={field.onChange} defaultValue={field.value}
															className="uppercase"
															label="Resident Status"
															placeholder="Select status"
															options={[
																{ id: "Membership", name: "Membership" },
																{ id: "Fellowship", name: "Fellowship" },
																{ id: "Post Fellowship", name: "Post Fellowship" },
																{ id: "Straight Fellowship", name: "Straight Fellowship" }
															]}
															disabled
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name="faculty"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<Input {...field} label="Faculty" placeholder="Faculty" className="uppercase" disabled />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name="subspecialty"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<Input {...field} label="Subspecialty" placeholder="Subspecialty" className="uppercase" disabled />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name="training_center"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<Input {...field} label="Training Center" placeholder="Korle Bu Teching Hospital" className="uppercase" disabled />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name="department"
											render={({ field }) => (
												<FormItem className="hidden">
													<FormControl>
														<SelectField onValueChange={field.onChange} defaultValue={field.value}
															className="uppercase"
															label="Specify Department"
															placeholder={isDepartmentLoading ? "Please wait..." : "Select department"}
															options={departments}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										{departmentValue === "1" && (
											<FormField
												control={form.control}
												name="department_other"
												render={({ field }) => (
													<FormItem>
														<FormControl>
															<Input {...field} label="Specify Department" placeholder="Dental" className="uppercase" />
														</FormControl>
														<FormMessage />
													</FormItem>
												)}
											/>

										)}

										<FormField
											control={form.control}
											name="academic_year"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<Input {...field} placeholder="e.g., 2025/2026" label="Academic Year" className="uppercase" disabled />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name="current_year"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<SelectField onValueChange={field.onChange} defaultValue={field.value}
															className="uppercase"
															label="Current Year"
															placeholder="Current Year"
															options={[
																{ id: "1", name: "Year 1" },
																{ id: "2", name: "Year 2" },
																{ id: "3", name: "Year 3" },
																{ id: "4", name: "Year 4" },
																{ id: "5", name: "Year 5" },
																{ id: "6", name: "Year 6" },
															]}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name="extension_requested"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<SelectField onValueChange={field.onChange} defaultValue={field.value}
															className="uppercase"
															label="Are you on Extension?"
															placeholder="Yes / No"
															options={[
																{ id: "1", name: "Yes" },
																{ id: "0", name: "No" },
															]}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										{extensionValue === "1" && (
											<>
												<FormField
													control={form.control}
													name="extension_start_date"
													render={({ field }) => (
														<FormItem>
															<FormControl>
																<Input
																	{...field}
																	type="date"
																	label="Extension Start Date"
																	placeholder="2024-05-19"
																	setDate={setExtensionStartDate}
																	onChange={(d) => {
																		console.log(d)
																		form.setValue('extension_start_date', extensionStartDate);
																		return field.onChange
																	}}
																/>
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
												<FormField
													control={form.control}
													name="extension_end_date"
													render={({ field }) => (
														<FormItem>
															<FormControl>
																<Input
																	{...field}
																	type="date"
																	label="Extension End Date"
																	setDate={setExtensionEndDate}
																	onChange={(d) => {
																		console.log(d)
																		form.setValue('extension_end_date', extensionEndDate);
																		return field.onChange
																	}}
																/>
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
											</>
										)}

										<FormField
											control={form.control}
											name="deferment_requested"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<SelectField onValueChange={field.onChange} defaultValue={field.value}
															className="uppercase"
															label="Have you Deferred?"
															placeholder="Yes / No"
															options={[
																{ id: "1", name: "Yes" },
																{ id: "0", name: "No" },
															]}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										{deferrmentValue === "1" && (
											<>
												<FormField
													control={form.control}
													name="deferment_start_date"
													render={({ field }) => (
														<FormItem>
															<FormControl>
																<Input
																	{...field}
																	type="date"
																	label="Deferment Start Date"
																	setDate={setDefermentStartDate}
																	onChange={() => {
																		form.setValue('deferment_start_date', defermentStartDate);
																		return field.onChange
																	}}
																/>
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
												<FormField
													control={form.control}
													name="deferment_end_date"
													render={({ field }) => (
														<FormItem>
															<FormControl>
																<Input
																	{...field}
																	type="date"
																	label="Deferment End Date"
																	setDate={setDefermentEndDate}
																	onChange={() => {
																		form.setValue('deferment_end_date', defermentEndDate);
																		return field.onChange
																	}}
																/>
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
											</>
										)}

										<FormField
											control={form.control}
											name="external_rotation"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<SelectField onValueChange={field.onChange} defaultValue={field.value}
															className="uppercase"
															label="Are you on External Rotation?"
															placeholder="Yes / No"
															options={[
																{ id: "1", name: "Yes" },
																{ id: "0", name: "No" },
															]}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										{rotationValue === "1" && (
											<>
												<FormField
													control={form.control}
													name="external_start_date"
													render={({ field }) => (
														<FormItem>
															<FormControl>
																<Input
																	{...field}
																	type="date"
																	label="Rotation Start Date"
																	setDate={setRotationStartDate}
																	onChange={() => {
																		form.setValue('external_start_date', rotationStartDate);
																		return field.onChange
																	}}
																/>
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
												<FormField
													control={form.control}
													name="external_end_date"
													render={({ field }) => (
														<FormItem>
															<FormControl>
																<Input
																	{...field}
																	type="date"
																	label="Rotation End Date"
																	setDate={setRotationEndDate}
																	onChange={() => {
																		form.setValue('external_end_date', rotationEndDate);
																		return field.onChange
																	}}
																/>
															</FormControl>
															<FormMessage />
														</FormItem>
													)}
												/>
											</>
										)}

										<FormField
											control={form.control}
											name="exam_resit"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<SelectField onValueChange={field.onChange} defaultValue={field.value}
															className="uppercase"
															label="Are you resitting for exams? (If registering in order to resit exams"
															placeholder="Yes / No"
															options={[
																{ id: "1", name: "Yes" },
																{ id: "0", name: "No" },
															]}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name="sponsoring_facility"
											render={({ field }) => (
												<FormItem>
													<FormControl>
														<SelectField onValueChange={field.onChange} defaultValue={field.value}
															className="uppercase"
															label="Name of Sponsoring Facility / Other Details"
															placeholder={isSponsorshipTypesLoading ? "Please wait..." : "Select sponsoring facility"}
															options={sponsorshipTypes}
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name="training_challenges"
											render={({ field }) => (
												<FormItem className="">
													<FormControl>
														<TextArea placeholder="Describe any challenges" {...field} label="Training Challenges / Comments" />
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>

										<FormField
											control={form.control}
											name="tuition_receipt"
											render={({ field }) => (
												<FormItem className={!["4", "11", "13"].includes(sponsoringValue) ? "hidden" : ""}>
													<FormControl>
														<Input
															onChange={(e) => field.onChange(e.target.files?.[0])}
															type="file"
															className="w-full"
															label="Attach Receipt of Tuition Fees Paid"
														/>
													</FormControl>
													<FormMessage />
												</FormItem>
											)}
										/>
									</div>

									<div className="mt-25 text-right">
										{/* disabled={mutation.isPending || !form.formState.isValid} loading={mutation.isPaused} */}
										<Button type="submit" disabled={mutation.isPending} loading={mutation.isPending}>
											Submit Registration
										</Button>
									</div>
								</form>
							</Form>
						</>
					)
				}
			</div>
		</DashboardLayout>
	);
};